import { useCallback, useEffect, useMemo, useState } from 'react'
import {Check, Crown, Loader2, Search, X} from 'lucide-react'
import toast from 'react-hot-toast'
import { lumi } from '@/lib/lumi'
import { useAuth } from '@/hooks/useAuth'
import type { ArticleComment } from '@/types'

type StatusFilter = 'all' | 'pending' | 'approved' | 'rejected'

function formatDate(iso?: string) {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return ''
  }
}

function statusLabel(status: ArticleComment['status']) {
  if (status === 'approved') return 'Aprovado'
  if (status === 'rejected') return 'Recusado'
  return 'Pendente'
}

export default function ModerateComments() {
  const { user, isAdmin } = useAuth()
  const [comments, setComments] = useState<ArticleComment[]>([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending')

  const canModerate = (comment: ArticleComment) => {
    if (isAdmin) return true
    const ids = [user?.id, user?.userId].filter(Boolean)
    return ids.includes(comment.articleAuthorId)
  }

  const fetchComments = useCallback(async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.article_comments.list({
        sort: { createdAt: -1 },
        limit: 400,
      })
      const items = (list || []) as ArticleComment[]
      setComments(items)
    } catch (e) {
      console.error('Erro ao carregar comentários:', e)
      toast.error('Não foi possível carregar os comentários.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchComments()
  }, [fetchComments])

  const visible = useMemo(() => {
    return comments.filter((comment) => {
      if (!canModerate(comment)) return false
      if (statusFilter !== 'all' && comment.status !== statusFilter) return false
      const q = search.trim().toLowerCase()
      if (!q) return true
      return (
        comment.authorName?.toLowerCase().includes(q) ||
        comment.authorEmail?.toLowerCase().includes(q) ||
        comment.content?.toLowerCase().includes(q) ||
        comment.articleTitle?.toLowerCase().includes(q)
      )
    })
  }, [comments, search, statusFilter, isAdmin, user?.id, user?.userId])

  const pendingCount = comments.filter((c) => canModerate(c) && c.status === 'pending').length

  const patchComment = async (comment: ArticleComment, data: Partial<ArticleComment>, success: string) => {
    if (!comment._id) return
    setBusyId(comment._id)
    try {
      await lumi.entities.article_comments.update(comment._id, {
        ...data,
        updatedAt: new Date().toISOString(),
      })
      setComments((prev) => prev.map((item) => (item._id === comment._id ? { ...item, ...data } : item)))
      toast.success(success)
    } catch (e) {
      console.error('Erro ao atualizar comentário:', e)
      toast.error('Não foi possível atualizar o comentário.')
    } finally {
      setBusyId(null)
    }
  }

  const handlePin = async (comment: ArticleComment) => {
    const nextPinned = !comment.isPinned
    setBusyId(comment._id)
    try {
      if (nextPinned) {
        const others = comments.filter(
          (item) => item.articleId === comment.articleId && item._id !== comment._id && item.isPinned
        )
        await Promise.all(
          others.map((item) =>
            lumi.entities.article_comments.update(item._id, {
              isPinned: false,
              updatedAt: new Date().toISOString(),
            })
          )
        )
        setComments((prev) =>
          prev.map((item) =>
            item.articleId === comment.articleId && item._id !== comment._id
              ? { ...item, isPinned: false }
              : item
          )
        )
      }
      await lumi.entities.article_comments.update(comment._id, {
        isPinned: nextPinned,
        status: nextPinned ? 'approved' : comment.status,
        updatedAt: new Date().toISOString(),
      })
      setComments((prev) =>
        prev.map((item) =>
          item._id === comment._id
            ? { ...item, isPinned: nextPinned, status: nextPinned ? 'approved' : item.status }
            : item
        )
      )
      toast.success(nextPinned ? 'Comentário fixado com coroa.' : 'Coroa removida.')
    } catch (e) {
      console.error('Erro ao fixar comentário:', e)
      toast.error('Não foi possível alterar o destaque.')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Comentários</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {isAdmin
            ? 'Aprove, recuse ou fixe com coroa os comentários de todas as matérias.'
            : 'Modere os comentários das suas próprias matérias.'}
          {pendingCount > 0 ? ` ${pendingCount} pendente(s).` : ''}
        </p>
      </div>

      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por matéria, nome ou texto..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          className="px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500"
        >
          <option value="pending">Pendentes</option>
          <option value="approved">Aprovados</option>
          <option value="rejected">Recusados</option>
          <option value="all">Todos</option>
        </select>
      </div>

      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 gap-2 text-gray-400">
            <Loader2 size={22} className="animate-spin text-red-600" />
            <span className="text-sm">A carregar comentários…</span>
          </div>
        ) : visible.length === 0 ? (
          <div className="py-16 text-center text-sm text-gray-400">Nenhum comentário neste filtro.</div>
        ) : (
          <div className="divide-y divide-gray-50 dark:divide-gray-800">
            {visible.map((comment) => {
              const busy = busyId === comment._id
              return (
                <article key={comment._id} className="px-5 py-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                        {comment.articleTitle || 'Matéria'}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {comment.authorName} · {comment.authorEmail} · {formatDate(comment.createdAt)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {comment.isPinned ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-amber-700 dark:text-amber-400">
                          <Crown size={12} />
                          Coroa
                        </span>
                      ) : null}
                      <span
                        className={`text-[11px] px-2 py-1 rounded-full font-semibold ${
                          comment.status === 'approved'
                            ? 'bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-400'
                            : comment.status === 'rejected'
                              ? 'bg-gray-100 dark:bg-gray-800 text-gray-500'
                              : 'bg-yellow-50 dark:bg-yellow-950 text-yellow-700 dark:text-yellow-400'
                        }`}
                      >
                        {statusLabel(comment.status)}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-200 mt-3 whitespace-pre-wrap leading-relaxed">
                    {comment.content}
                  </p>
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    {comment.status !== 'approved' ? (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => patchComment(comment, { status: 'approved' }, 'Comentário aprovado.')}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
                      >
                        {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                        Aprovar
                      </button>
                    ) : null}
                    {comment.status !== 'rejected' ? (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => patchComment(comment, { status: 'rejected', isPinned: false }, 'Comentário recusado.')}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50"
                      >
                        <X size={12} />
                        Recusar
                      </button>
                    ) : null}
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => handlePin(comment)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border disabled:opacity-50 ${
                        comment.isPinned
                          ? 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-300'
                          : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-amber-300'
                      }`}
                    >
                      <Crown size={12} />
                      {comment.isPinned ? 'Remover coroa' : 'Fixar com coroa'}
                    </button>
                  </div>
                </article>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
